import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterSliceState, FilterUpdate, FilterState, PopUpData, SortType } from './interfaces';
import { MotionTypeOptions } from '../../interfaces';
import { RootState } from '../../app/store';
import { getResults, getFilterByMotionType, getPopUpResult } from './api';

export const filtersState: FilterState = {
  motionType: 'VERTICAL_MOTION_FREE_FALLING_MASS',
  movingMass: 0,
  impactVelocity: 0,
  startVelocity: 0,
  cyclesPerHour: 0,
  numShockAbsorbers: 1,
  fixedStop: true,
  soft: null,
  robust: null,
  cushioningAdjustments: ['AUTOMATIC', 'MANUAL'],
  cushioningTechnologies: ['HYDRAULIC', 'MECHANICAL'],
  mountingType: 'LOCK_NUT',
  forBatteryProduction: null,
  recommendedResults: false,
  momentOfInertia: 0,
  dropHeight: 0,
  startAngularVelocity: 0,
  angularVelocity: 0,
  lengthOfStick: 0,
  radiusToCylinder: 0,
  distanceToShockAbsorber: 0,
  distanceToCenterOfMass: 0,
  externalDriveTorque: 0,
  force: 0,
  coefficientOfFriction: 0,
  angleOfIncline: 0,
};

export const fetchFilterByMotionType = createAsyncThunk('filter/fetchFilter', async (motionType: MotionTypeOptions) => {
  const response = await getFilterByMotionType(motionType);

  return response.data;
});

export const fetchResults = createAsyncThunk('filter/fetchResults', async (filters: FilterState) => {
  const response = await getResults(filters);

  return response.data;
});

export const fetchPopUpResult = createAsyncThunk('filter/fetchPopUpResult', async (popUpInfo: PopUpData) => {
  const { popUpFilters, motionType } = popUpInfo;
  const response = await getPopUpResult(popUpFilters, motionType);

  return response.data;
});

const initialState: FilterSliceState = {
  isResultsLoading: 'idle',
  isPopUpResultLoading: 'idle',
  isFiltersLoading: 'idle',
  filters: filtersState,
  popupResult: {
    filterName: '',
    value: 0,
  },
  sectionFilters: [],
  resultFilters: [],
  shockAbsorbers: null,
  sortBy: 'ASCENDING_PRICE',
};

const updateFilterValue = (state: FilterSliceState, action: PayloadAction<FilterUpdate>) => {
  const { name, newValue } = action.payload;

  state.filters = {
    ...state.filters,
    [name]: newValue,
  };
};

const sortByResults = (
  state: FilterSliceState,
  action: PayloadAction<{
    sort: SortType;
  }>,
) => {
  const { sort } = action.payload;
  state.sortBy = sort;
};

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    updateFilter: updateFilterValue,
    setSortBy: sortByResults,
    clearFilter: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterByMotionType.pending, (state) => {
        state.isFiltersLoading = 'pending';
      })
      .addCase(fetchFilterByMotionType.fulfilled, (state, action) => {
        state.isFiltersLoading = 'success';
        const allFilters = action.payload;

        // // Result page filter keys
        const resultFilterKeys =
          'fixedStop cushioningReaction cushioningTechnologies mountingType additionalFunction recommendedResults cushioningAdjustments';

        allFilters.map((filter) =>
          resultFilterKeys.includes(filter.key)
            ? (state.resultFilters = [...state.resultFilters, filter])
            : (state.sectionFilters = [...state.sectionFilters, filter]),
        );

        // Sort by prop from object.sort
        state.resultFilters.sort((a, b) => (a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0));
        state.sectionFilters.sort((a, b) => (a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0));
      })
      .addCase(fetchFilterByMotionType.rejected, (state) => {
        state.isFiltersLoading = 'error';
      })
      .addCase(fetchResults.pending, (state) => {
        state.isResultsLoading = 'pending';
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        const { shockAbsorbers } = action.payload;
        state.isResultsLoading = 'success';

        if (shockAbsorbers && shockAbsorbers.length > 0) {
          state.shockAbsorbers = action.payload;
        } else {
          state.shockAbsorbers = { shockAbsorbers };
        }
      })
      .addCase(fetchResults.rejected, (state) => {
        state.isResultsLoading = 'error';
      })
      .addCase(fetchPopUpResult.pending, (state) => {
        state.isPopUpResultLoading = 'pending';
      })
      .addCase(fetchPopUpResult.fulfilled, (state, action) => {
        state.isPopUpResultLoading = 'success';
        const { payload } = action;

        if (payload.impactVelocity) {
          state.popupResult = {
            filterName: 'impactVelocity',
            value: payload.impactVelocity,
          };
        }

        if (
          payload.angularVelocity &&
          state.filters.motionType === 'ROTATIONAL_MOTION_ROTATING_LEVER_ON_HORIZONTAL_PLANE_WITH_DRIVING_TORQUE'
        ) {
          state.popupResult = {
            filterName: 'impactVelocity',
            value: payload.angularVelocity,
          };
        }

        if (payload.force) {
          state.popupResult = {
            filterName: 'force',
            value: payload.force,
          };
        }

        if (payload.coefficientOffFriction) {
          state.popupResult = {
            filterName: 'coefficientOfFriction',
            value: payload.coefficientOffFriction,
          };
        }

        if (payload.startAngularVelocity) {
          state.popupResult = {
            filterName: 'startAngularVelocity',
            value: payload.startAngularVelocity,
          };
        }

        if (
          payload.angularVelocity &&
          state.filters.motionType !== 'ROTATIONAL_MOTION_ROTATING_LEVER_ON_HORIZONTAL_PLANE_WITH_DRIVING_TORQUE'
        ) {
          state.popupResult = {
            filterName: 'angularVelocity',
            value: payload.angularVelocity,
          };
        }

        if (payload.dropHeight) {
          state.popupResult = {
            filterName: 'dropHeight',
            value: payload.dropHeight,
          };
        }
      })
      .addCase(fetchPopUpResult.rejected, (state) => {
        state.isPopUpResultLoading = 'error';
      });
  },
});

export const { updateFilter, clearFilter, setSortBy } = filterSlice.actions;

export const selectedFilters = (state: RootState) => state.filters.filters;
export const sectionFilters = (state: RootState) => state.filters.sectionFilters;
export const popupResultValues = (state: RootState) => state.filters.popupResult;
export const resultFilters = (state: RootState) => state.filters.resultFilters;
export const isResultsLoading = (state: RootState) => state.filters.isResultsLoading;
export const isFiltersLoading = (state: RootState) => state.filters.isFiltersLoading;
export const isPopUpLoading = (state: RootState) => state.filters.isPopUpResultLoading;
export const shockAbsorbersSelected = (state: RootState) => state.filters.shockAbsorbers;
export const sortBy = (state: RootState) => state.filters.sortBy;

export default filterSlice.reducer;
